import React, { memo } from "react"
import { useDroppable } from "@dnd-kit/core"
import { isPublicHoliday, getPublicHolidayNames } from "./utils"

/**
 * DroppableDayCell component wraps a table cell to make it a valid drop target
 * for drag-and-drop operations.
 *
 * @param {Object} props
 * @param {number} props.dayIndex - The day index (0-5 for Mon-Sat)
 * @param {number} props.surveyorId - The surveyor ID this cell belongs to
 * @param {Date} props.date - The date this cell represents
 * @param {Object} props.publicHolidaysByDate - Public holidays data indexed by date
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.rowSpan - Optional row span for the cell
 * @param {React.ReactNode} props.children - Cell content
 */
function DroppableDayCell({
  dayIndex,
  surveyorId,
  date,
  publicHolidaysByDate,
  className = "",
  rowSpan,
  children
}) {
  const droppableId = `day-${surveyorId}-${dayIndex}`

  // Check if this date is a public holiday
  const isHoliday = isPublicHoliday(date, publicHolidaysByDate)
  const holidayNames = getPublicHolidayNames(date, publicHolidaysByDate)

  const { isOver, setNodeRef } = useDroppable({
    id: droppableId,
    data: {
      type: "day-cell",
      dayIndex,
      surveyorId,
      date,
      isPublicHoliday: isHoliday
    },
    disabled: isHoliday // Disable dropping on public holidays
  })

  // Add visual feedback when dragging over
  const dropClasses = [
    className,
    isOver && !isHoliday ? "drop-target-active" : "",
    isHoliday ? "public-holiday-cell" : ""
  ].filter(Boolean).join(" ")

  // Create title for holiday tooltip
  const title = isHoliday && holidayNames.length > 0
    ? `Public Holiday: ${holidayNames.join(", ")}`
    : undefined

  return (
    <td
      ref={setNodeRef}
      className={dropClasses}
      rowSpan={rowSpan}
      title={title}>
      {isHoliday && (
        <div className="holiday-indicator">
          <span className="holiday-icon" aria-label="Public Holiday">H</span>
          <span className="holiday-text">Holiday</span>
        </div>
      )}
      {children}
    </td>
  )
}

export default memo(DroppableDayCell)
